import React, { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, Loader2, Trash2, CheckCircle, Sparkles, AlertCircle, FileText, RefreshCw,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";

const API = `${window.location.origin}/api`;

/**
 * InvoiceBulkUploadDialog — Phase-2 Invoice OCR pipeline
 *
 * Multi-file upload → backend calls Gemini 3 Flash → DRAFT rows in invoice_drafts.
 * Admin reviews each draft inline, optionally edits extracted data, then Approves.
 * Approval commits Customer + Enquiry + Invoice + Payment records.
 */
export const InvoiceBulkUploadDialog = ({ open, onClose, authHeaders, onCommitted }) => {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [activeDraft, setActiveDraft] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const loadPendingDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    try {
      const res = await axios.get(`${API}/admin/invoice-drafts?status=pending_review`, authHeaders);
      setDrafts(res.data?.drafts || []);
    } catch (e) {
      toast.error("Failed to load drafts");
    } finally {
      setLoadingDrafts(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (open) loadPendingDrafts();
  }, [open, loadPendingDrafts]);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      arr.forEach((f) => form.append("files", f));
      const res = await axios.post(`${API}/admin/invoices/bulk-upload`, form, {
        headers: { ...(authHeaders?.headers || {}), "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(`Processed ${res.data.count} file${res.data.count === 1 ? "" : "s"} — review the drafts below.`);
      await loadPendingDrafts();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const approve = async (draft) => {
    try {
      const res = await axios.post(`${API}/admin/invoice-drafts/${draft.id}/approve`, {}, authHeaders);
      toast.success(`Approved — customer+job+invoice created (${res.data.refs?.invoice_id?.slice(0, 6) || "ok"})`);
      setActiveDraft(null);
      await loadPendingDrafts();
      onCommitted?.();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };

  const reject = async (draft) => {
    if (!window.confirm(`Reject draft from ${draft.source_filename}?`)) return;
    try {
      await axios.delete(`${API}/admin/invoice-drafts/${draft.id}`, authHeaders);
      toast.success("Draft deleted");
      setActiveDraft(null);
      await loadPendingDrafts();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const saveEdits = async (draft) => {
    setSavingDraft(true);
    try {
      await axios.patch(`${API}/admin/invoice-drafts/${draft.id}`, { extracted: draft.extracted }, authHeaders);
      toast.success("Draft saved");
      await loadPendingDrafts();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl" data-testid="invoice-bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle>Bulk upload invoices</DialogTitle>
          <DialogDescription>
            Drop PDF or photographed invoices. Gemini extracts the data, you review each draft, then Approve to create the customer / job / invoice / payment records.
          </DialogDescription>
        </DialogHeader>

        {/* Dropzone */}
        <div
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer?.files); }}
          className="border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer hover:bg-muted/30"
          data-testid="invoice-ocr-dropzone"
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Extracting invoices — can take 10-30s per file…</span>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 mx-auto mb-1 text-muted-foreground" />
              <p className="font-medium text-sm">Drop PDFs or photos here, or click to select</p>
              <p className="text-xs text-muted-foreground mt-0.5">Supports .pdf, .jpg, .png, .heic · multi-file</p>
            </>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="application/pdf,image/*"
            hidden
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            data-testid="invoice-ocr-file-input"
          />
        </div>

        {/* Drafts list */}
        <div className="flex items-center justify-between mt-2">
          <p className="text-sm font-medium">Pending drafts ({drafts.length})</p>
          <Button size="sm" variant="ghost" onClick={loadPendingDrafts} disabled={loadingDrafts}>
            {loadingDrafts ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>

        {drafts.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-4">
            No pending drafts. Uploaded invoices will appear here for review.
          </div>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {drafts.map((d) => {
              const ex = d.extracted || {};
              return (
                <div key={d.id} className="border rounded-md p-3" data-testid={`invoice-draft-row-${d.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{ex.customer_name || d.source_filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {ex.invoice_reference || "—"} · £{Number(ex.total || 0).toFixed(2)} · {ex.payment_status || "—"}
                      </p>
                    </div>
                    {d.extract_error && (
                      <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 text-xs" title={d.extract_error}>
                        <AlertCircle className="h-3 w-3 mr-1" />OCR error
                      </Badge>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setActiveDraft(d)} data-testid={`invoice-draft-review-${d.id}`}>
                      Review
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => reject(d)} data-testid={`invoice-draft-reject-${d.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>

      {/* Review dialog */}
      {activeDraft && (
        <DraftReviewDialog
          draft={activeDraft}
          onClose={() => setActiveDraft(null)}
          onChange={(next) => setActiveDraft(next)}
          onSave={() => saveEdits(activeDraft)}
          onApprove={() => approve(activeDraft)}
          onReject={() => reject(activeDraft)}
          saving={savingDraft}
        />
      )}
    </Dialog>
  );
};


const DraftReviewDialog = ({ draft, onClose, onChange, onSave, onApprove, onReject, saving }) => {
  const ex = draft.extracted || {};
  const set = (k, v) => onChange({ ...draft, extracted: { ...ex, [k]: v } });
  const setLine = (idx, patch) => {
    const items = [...(ex.line_items || [])];
    items[idx] = { ...items[idx], ...patch };
    set("line_items", items);
  };
  const addLine = () => set("line_items", [...(ex.line_items || []), { description: "", quantity: 1, unit_cost: 0, total: 0 }]);
  const removeLine = (idx) => set("line_items", (ex.line_items || []).filter((_, i) => i !== idx));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="invoice-draft-review-dialog">
        <DialogHeader>
          <DialogTitle>Review draft — {draft.source_filename}</DialogTitle>
          <DialogDescription>
            Edit any fields Gemini got wrong, then Approve to create the real records.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><Label className="text-xs">Customer name</Label><Input value={ex.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} data-testid="draft-customer-name" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={ex.customer_phone || ""} onChange={(e) => set("customer_phone", e.target.value)} /></div>
            <div><Label className="text-xs">Email</Label><Input value={ex.customer_email || ""} onChange={(e) => set("customer_email", e.target.value)} /></div>
            <div><Label className="text-xs">Invoice reference</Label><Input value={ex.invoice_reference || ""} onChange={(e) => set("invoice_reference", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Address</Label><Input value={ex.customer_address || ""} onChange={(e) => set("customer_address", e.target.value)} /></div>
            <div><Label className="text-xs">Invoice date (YYYY-MM-DD)</Label><Input value={ex.invoice_date || ""} onChange={(e) => set("invoice_date", e.target.value)} placeholder="2025-04-22" /></div>
            <div><Label className="text-xs">Due date</Label><Input value={ex.due_date || ""} onChange={(e) => set("due_date", e.target.value)} /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Line items</Label>
              <Button size="sm" variant="ghost" onClick={addLine}>+ Add line</Button>
            </div>
            <div className="border rounded">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left p-1.5">Description</th>
                    <th className="text-left p-1.5 w-14">Qty</th>
                    <th className="text-left p-1.5 w-20">Unit £</th>
                    <th className="text-left p-1.5 w-20">Total £</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {(ex.line_items || []).map((li, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="p-1"><Input value={li.description || ""} onChange={(e) => setLine(idx, { description: e.target.value })} className="h-7 text-xs" /></td>
                      <td className="p-1"><Input type="number" step="0.1" value={li.quantity ?? 1} onChange={(e) => setLine(idx, { quantity: parseFloat(e.target.value) || 0 })} className="h-7 text-xs" /></td>
                      <td className="p-1"><Input type="number" step="0.01" value={li.unit_cost ?? 0} onChange={(e) => setLine(idx, { unit_cost: parseFloat(e.target.value) || 0 })} className="h-7 text-xs" /></td>
                      <td className="p-1"><Input type="number" step="0.01" value={li.total ?? 0} onChange={(e) => setLine(idx, { total: parseFloat(e.target.value) || 0 })} className="h-7 text-xs" /></td>
                      <td className="p-1 text-center"><button type="button" onClick={() => removeLine(idx)} className="text-destructive text-xs">×</button></td>
                    </tr>
                  ))}
                  {!(ex.line_items || []).length && (
                    <tr><td colSpan={5} className="p-2 text-center text-xs text-muted-foreground">No line items detected — add one manually.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><Label className="text-xs">Subtotal £</Label><Input type="number" step="0.01" value={ex.subtotal ?? ""} onChange={(e) => set("subtotal", parseFloat(e.target.value) || 0)} /></div>
            <div><Label className="text-xs">VAT £</Label><Input type="number" step="0.01" value={ex.vat ?? ""} onChange={(e) => set("vat", parseFloat(e.target.value) || 0)} /></div>
            <div><Label className="text-xs">Total £</Label><Input type="number" step="0.01" value={ex.total ?? ""} onChange={(e) => set("total", parseFloat(e.target.value) || 0)} data-testid="draft-total" /></div>
            <div><Label className="text-xs">Amount paid £</Label><Input type="number" step="0.01" value={ex.amount_paid ?? ""} onChange={(e) => set("amount_paid", parseFloat(e.target.value) || 0)} /></div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Payment status</Label><Input value={ex.payment_status || ""} onChange={(e) => set("payment_status", e.target.value)} placeholder="paid | partial | unpaid" /></div>
            <div><Label className="text-xs">Payment method</Label><Input value={ex.payment_method || ""} onChange={(e) => set("payment_method", e.target.value)} /></div>
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={ex.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="text-destructive mr-auto" onClick={onReject} data-testid="draft-reject-btn">
            <Trash2 className="h-4 w-4 mr-1" />Reject
          </Button>
          <Button variant="outline" onClick={onSave} disabled={saving} data-testid="draft-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Save draft
          </Button>
          <Button onClick={onApprove} data-testid="draft-approve-btn">
            <CheckCircle className="h-4 w-4 mr-1" />Approve &amp; create records
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceBulkUploadDialog;
